import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AppointmentParticipantsEditor, {
  makeClientEntry,
  makeExternalEntry,
} from "components/AppointmentParticipantsEditor";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { API_URL } from "const/constants";
import { P } from "const/permissions";
import { hasPermission } from "utils/permissionUtils";
import { FROM_EMAILS, DEFAULT_FROM_EMAIL } from "const/emailSenders";
import useKothar from "context/useKothar";
import { usePaginatedSearch } from "hooks/usePaginatedSearch";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { getPaginatedClientData } from "utils/clientService";
import {
  hasNotifyRelevantChanges,
  invalidateEmailLogQueries,
} from "views/admin/Appointments/appointmentNotifyUtils";
import {
  APPOINTMENT_VISIBILITY,
  validateAppointmentEndTime,
} from "views/admin/Appointments/appointmentUtils";
import AppointmentConflictDialog from "views/admin/Appointments/AppointmentConflictDialog";

export const APPOINTMENT_STATUSES = ["SCHEDULED", "COMPLETED", "CANCELLED", "RESCHEDULED"];

const EMPTY_FORM = {
  title: "",
  description: "",
  appointmentDate: "",
  appointmentTime: "",
  appointmentEndTime: "",
  location: "",
  meetLink: "",
  withUserId: null,
  ccEmails: "",
  fromEmail: DEFAULT_FROM_EMAIL,
  status: "SCHEDULED",
  visibility: APPOINTMENT_VISIBILITY.ORG,
  notes: "",
};

const buildFormFromAppointment = (appt) =>
  appt
    ? {
        title: appt.title || "",
        description: appt.description || "",
        appointmentDate: appt.appointmentDate || "",
        appointmentTime: appt.appointmentTime || "",
        appointmentEndTime: appt.appointmentEndTime || "",
        location: appt.location || "",
        meetLink: appt.meetLink || "",
        withUserId: appt.withUserId ?? null,
        ccEmails: appt.ccEmails || "",
        fromEmail: appt.fromEmail || DEFAULT_FROM_EMAIL,
        status: appt.status || "SCHEDULED",
        visibility: appt.visibility || APPOINTMENT_VISIBILITY.ORG,
        notes: appt.notes || "",
      }
    : { ...EMPTY_FORM };

const fetchClientPage = ({ page, size, search }) =>
  getPaginatedClientData({ pageIndex: page, pageSize: size, searchText: search });

// Turn an appointment's attendees into editor entries (handles legacy single-client rows).
const buildEntriesFromAppointment = (appt) => {
  const list = Array.isArray(appt?.participants) ? appt.participants : [];
  const isManualLegacy =
    appt?.existingClient === false || (!appt?.clientId && (appt?.clientName || appt?.clientEmail));
  const source = list.length
    ? list
    : [
        {
          existingClient: !isManualLegacy,
          clientId: appt?.clientId ?? null,
          clientName: appt?.clientName || "",
          clientEmail: appt?.clientEmail || "",
          clientPhoneNumber: appt?.clientPhoneNumber || "",
        },
      ];
  return source.map((pp) =>
    pp.existingClient && pp.clientId
      ? makeClientEntry({ id: pp.clientId, label: pp.clientName, email: pp.clientEmail })
      : makeExternalEntry({
          name: pp.clientName,
          email: pp.clientEmail,
          phone: pp.clientPhoneNumber,
        })
  );
};

const AppointmentFormDialog = ({
  open,
  onClose,
  appointment = null,
  clientId = null,
  defaultValues,
  onSaved,
  invalidateQueryKeys = [],
}) => {
  const editId = appointment?.id ?? null;
  const queryClient = useQueryClient();
  const [{ usersList }] = useKothar();

  const [form, setForm] = useState(() => buildFormFromAppointment(appointment));
  const [entries, setEntries] = useState(() => []);
  const [endTimeError, setEndTimeError] = useState("");
  const [conflicts, setConflicts] = useState([]);
  const [conflictOpen, setConflictOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [sendWhatsApp, setSendWhatsApp] = useState(false);
  const canSendWhatsApp = hasPermission(P.MESSAGING.WHATSAPP_SEND);
  const showParticipantsEditor = !clientId || !!editId;

  useEffect(() => {
    if (!open) return;
    setConflicts([]);
    setConflictOpen(false);
    setPendingPayload(null);
    setSendWhatsApp(false);
    if (appointment) {
      setForm(buildFormFromAppointment(appointment));
      setEntries(buildEntriesFromAppointment(appointment));
    } else {
      setForm({ ...EMPTY_FORM, ...(defaultValues || {}) });
      setEntries([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editId]);

  const withUserOptions = useMemo(
    () => (usersList ?? []).map((u) => ({ id: u.id, label: u.name || u.email })),
    [usersList]
  );

  const {
    inputValue: clientSearchInput,
    setInputValue: setClientSearchInput,
    options: clientSearchResults,
    loading: clientSearchLoading,
    listboxProps: clientListboxProps,
  } = usePaginatedSearch(fetchClientPage, { pageSize: 20 });

  const clientOptions = useMemo(
    () =>
      (clientSearchResults ?? []).map((c) => ({
        id: c.id,
        label: c.name || c.email || `Client #${c.id}`,
        email: c.email || "",
      })),
    [clientSearchResults]
  );

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const { mutate, isLoading } = useMutation({
    mutationFn: (payload) =>
      editId
        ? axios.put(`${API_URL}/appointments/${editId}`, payload)
        : axios.post(`${API_URL}/appointments`, payload),
    onSuccess: (res, variables) => {
      toast.success(editId ? "Appointment updated" : "Appointment created");

      const involvedClientIds = new Set();
      if (clientId) involvedClientIds.add(Number(clientId));
      if (variables?.clientId) involvedClientIds.add(Number(variables.clientId));
      (variables?.participants || []).forEach((p) => {
        if (p.clientId) involvedClientIds.add(Number(p.clientId));
      });

      const keysToInvalidate = [
        ["all-appointments"],
        ...[...involvedClientIds].flatMap((id) => [
          ["client-appointments", id],
          ["client-summary", id],
        ]),
        ...invalidateQueryKeys,
      ];
      keysToInvalidate.forEach((key) => queryClient.invalidateQueries(key));
      invalidateEmailLogQueries(queryClient);
      onSaved?.(res?.data?.data);
      onClose?.();
    },
    onError: () => toast.error("Failed to save appointment"),
  });

  const { mutateAsync: checkConflicts, isLoading: isCheckingConflicts } = useMutation({
    mutationFn: (body) =>
      axios.post(`${API_URL}/appointments/check-conflicts`, body).then((r) => r.data?.data ?? []),
  });

  const notifyChanges = useMemo(
    () => (editId ? hasNotifyRelevantChanges(appointment, form, entries) : false),
    [appointment, editId, entries, form]
  );

  // Existing client ids on the candidate (primary or participants) — external attendees have no id.
  const buildConflictBody = () => {
    const clientIds = [];
    if (clientId && !editId) {
      clientIds.push(Number(clientId));
    } else {
      entries.forEach((e) => {
        if (!e.external && e.id != null) clientIds.push(Number(e.id));
      });
    }
    return {
      appointmentDate: form.appointmentDate,
      appointmentTime: form.appointmentTime || null,
      appointmentEndTime: form.appointmentEndTime || null,
      withUserId: form.withUserId ?? null,
      clientIds,
      excludeAppointmentId: editId,
    };
  };

  const buildPayload = (sendNotificationEmail = false) => {
    const base = {
      ...form,
      visibility: form.visibility || APPOINTMENT_VISIBILITY.ORG,
      ccEmails: (form.ccEmails || "").trim(),
      sendWhatsApp,
      ...(editId ? { sendNotificationEmail } : {}),
    };

    if (clientId && !editId) {
      return { ...base, existingClient: true, clientId: Number(clientId) };
    }

    const built = entries.map((e) =>
      e.external
        ? {
            existingClient: false,
            clientName: e.name,
            clientEmail: e.email,
            clientPhoneNumber: e.phone || null,
          }
        : { existingClient: true, clientId: Number(e.id) }
    );

    return { ...base, participants: built };
  };

  const handleSubmit = async (sendNotificationEmail = false) => {
    if (!form.title.trim() || !form.appointmentDate.trim()) {
      toast.error("Title and date are required");
      return;
    }

    const endErr = validateAppointmentEndTime(form.appointmentTime, form.appointmentEndTime);
    if (endErr) {
      setEndTimeError(endErr);
      toast.error(endErr);
      return;
    }
    setEndTimeError("");

    if (!(clientId && !editId) && !entries.length) {
      toast.error("Add at least one client");
      return;
    }

    const payload = buildPayload(sendNotificationEmail);

    // Warn (soft) if this clashes with an existing appointment on the same client or officer.
    // A failed check must not block scheduling.
    try {
      const found = await checkConflicts(buildConflictBody());
      if (Array.isArray(found) && found.length) {
        setConflicts(found);
        setPendingPayload(payload);
        setConflictOpen(true);
        return;
      }
    } catch (_) {
      // ignore — proceed to save
    }

    mutate(payload);
  };

  const handleProceedAnyway = () => {
    setConflictOpen(false);
    if (pendingPayload) mutate(pendingPayload);
    setPendingPayload(null);
  };

  const handleCancelConflict = () => {
    setConflictOpen(false);
    setPendingPayload(null);
    setConflicts([]);
  };

  return (
    <>
      <Dialog open={open} maxWidth="md" fullWidth>
        <DialogTitle>{editId ? "Edit Appointment" : "New Appointment"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {showParticipantsEditor && (
              <AppointmentParticipantsEditor
                clientOptions={clientOptions}
                searchInputValue={clientSearchInput}
                onSearchInputChange={setClientSearchInput}
                searchLoading={clientSearchLoading}
                listboxProps={clientListboxProps}
                value={entries}
                onChange={setEntries}
              />
            )}

            <TextField
              label="Title *"
              value={form.title}
              onChange={set("title")}
              fullWidth
              size="small"
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={set("description")}
              fullWidth
              size="small"
              multiline
              rows={2}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Date *"
                type="date"
                value={form.appointmentDate}
                onChange={set("appointmentDate")}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Start time"
                type="time"
                value={form.appointmentTime}
                onChange={(e) => {
                  const nextTime = e.target.value;
                  setForm((prev) => {
                    const next = { ...prev, appointmentTime: nextTime };
                    if (!nextTime.trim()) {
                      next.appointmentEndTime = "";
                    }
                    return next;
                  });
                  setEndTimeError("");
                }}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End time (optional)"
                type="time"
                value={form.appointmentEndTime}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, appointmentEndTime: e.target.value }));
                  setEndTimeError("");
                }}
                disabled={!form.appointmentTime?.trim()}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                error={!!endTimeError}
                helperText={
                  endTimeError ||
                  (!form.appointmentTime?.trim() ? "Set a start time first" : undefined)
                }
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Location"
                value={form.location}
                onChange={set("location")}
                fullWidth
                size="small"
              />
              <TextField
                label="Meet Link"
                value={form.meetLink}
                onChange={set("meetLink")}
                fullWidth
                size="small"
              />
            </Stack>
            <Autocomplete
              options={withUserOptions}
              getOptionLabel={(o) => o.label}
              value={withUserOptions.find((u) => u.id === form.withUserId) ?? null}
              onChange={(_, val) => setForm((prev) => ({ ...prev, withUserId: val?.id ?? null }))}
              renderInput={(params) => (
                <TextField {...params} label="Appointment With" size="small" fullWidth />
              )}
              isOptionEqualToValue={(o, v) => o.id === v.id}
            />
            <FormControl>
              <Typography component="legend" variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Visibility
              </Typography>
              <RadioGroup
                row
                value={form.visibility || APPOINTMENT_VISIBILITY.ORG}
                onChange={(e) => setForm((prev) => ({ ...prev, visibility: e.target.value }))}
              >
                <FormControlLabel
                  value={APPOINTMENT_VISIBILITY.ORG}
                  control={<Radio size="small" />}
                  label="Share with organization"
                />
                <FormControlLabel
                  value={APPOINTMENT_VISIBILITY.PRIVATE}
                  control={<Radio size="small" />}
                  label="Private — only me and the officer it's with"
                />
              </RadioGroup>
              <FormHelperText>
                Private appointments are visible only to you and the selected &quot;Appointment
                With&quot; officer.
              </FormHelperText>
            </FormControl>
            <Autocomplete
              options={FROM_EMAILS}
              value={form.fromEmail || DEFAULT_FROM_EMAIL}
              onChange={(_, val) =>
                setForm((prev) => ({ ...prev, fromEmail: val || DEFAULT_FROM_EMAIL }))
              }
              disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Send From"
                  size="small"
                  fullWidth
                  helperText="Sender address for appointment & reminder emails"
                />
              )}
              isOptionEqualToValue={(o, v) => o === v}
            />
            <TextField
              label="CC Emails (comma-separated)"
              value={form.ccEmails}
              onChange={set("ccEmails")}
              fullWidth
              size="small"
              placeholder="email1@example.com, email2@example.com"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={form.status} onChange={set("status")} label="Status">
                {APPOINTMENT_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {editId && (
              <TextField
                label="Notes"
                value={form.notes}
                onChange={set("notes")}
                fullWidth
                size="small"
                multiline
                rows={4}
                placeholder="What was discussed, outcomes, follow-ups..."
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
          {canSendWhatsApp ? (
            <FormControlLabel
              sx={{ mr: "auto", ml: 1 }}
              control={
                <Checkbox
                  checked={sendWhatsApp}
                  onChange={(event) => setSendWhatsApp(event.target.checked)}
                />
              }
              label="Also send WhatsApp to each opted-in client with a mobile number"
            />
          ) : null}
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          {editId ? (
            <>
              <Button
                variant="outlined"
                onClick={() => handleSubmit(false)}
                disabled={isLoading || isCheckingConflicts}
              >
                Update
              </Button>
              {notifyChanges && (
                <Button
                  variant="contained"
                  onClick={() => handleSubmit(true)}
                  disabled={isLoading || isCheckingConflicts}
                >
                  Update & send email
                </Button>
              )}
            </>
          ) : (
            <Button
              variant="contained"
              onClick={() => handleSubmit()}
              disabled={isLoading || isCheckingConflicts}
            >
              Create
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <AppointmentConflictDialog
        open={conflictOpen}
        conflicts={conflicts}
        onCancel={handleCancelConflict}
        onProceed={handleProceedAnyway}
        isSubmitting={isLoading}
      />
    </>
  );
};

export default AppointmentFormDialog;
